/**
 * Core Page Registrations
 *
 * Registers pages that live in neuro-core's own frontend (src/pages/)
 * directly into the PageRegistry so PluginPageHost can render them.
 *
 * These are NOT plugin pages — they are first-party core features whose
 * UI lives in the monolith.  Previously some of these were incorrectly
 * wrapped by thin plugin shims that just re-exported the core component
 * (a core→plugin layer violation).  This module makes them honest core
 * pages.
 */

import { lazy } from 'react';
import { usePageRegistry } from './registries/page-registry';
import type { PluginPageDefinition } from './registries/page-registry';

// ── Workflow Pages ──────────────────────────────────────────────
// Backend routes are served by neuro-core controllers:
//   WorkflowDefinitionsController (definitions + catalog),
//   WorkflowExecutionController (runs + insights), WorkflowTemplateController,
//   WorkflowAIController, KernelWorkflowCockpitController (Runs workspace)

const WorkflowsWorkspace = lazy(() => import('../pages/workflows/workspace/WorkflowsWorkspace'));
const WorkflowBuilderPage = lazy(() => import('../pages/workflows/builder/WorkflowBuilderPage'));
const WorkflowRunsRedirect = lazy(() => import('../pages/workflows/runs/WorkflowRunsRedirect'));
const AgentCockpitPage = lazy(() => import('../pages/agents/AgentCockpitPage'));

const CORE_PAGES: PluginPageDefinition[] = [
  {
    id: 'core:workflows',
    pluginId: 'core',
    path: '/workflows',
    title: 'Workflows',
    icon: 'Workflow',
    component: WorkflowsWorkspace,
    showInNav: true,
    order: 57,
  },
  {
    id: 'core:workflows/templates',
    pluginId: 'core',
    path: '/workflows/templates',
    title: 'Workflow Templates',
    icon: 'LayoutTemplate',
    component: WorkflowsWorkspace,
    showInNav: false,
  },
  {
    id: 'core:workflows/new',
    pluginId: 'core',
    path: '/workflows/new',
    title: 'New Workflow',
    icon: 'Workflow',
    component: WorkflowsWorkspace,
    showInNav: false,
  },
  {
    id: 'core:workflows/draft',
    pluginId: 'core',
    path: '/workflows/draft',
    title: 'Workflow Editor',
    icon: 'Workflow',
    component: WorkflowBuilderPage,
    showInNav: false,
  },
  {
    id: 'core:workflows/:workflowId',
    pluginId: 'core',
    path: '/workflows/:workflowId',
    title: 'Workflow',
    icon: 'Workflow',
    component: WorkflowsWorkspace,
    showInNav: false,
  },
  {
    id: 'core:workflows/:id/edit',
    pluginId: 'core',
    path: '/workflows/:workflowId/edit',
    title: 'Edit Workflow',
    icon: 'Workflow',
    component: WorkflowBuilderPage,
    showInNav: false,
  },
  {
    id: 'core:workflows/:id/runs',
    pluginId: 'core',
    path: '/workflows/:id/runs',
    title: 'Run History',
    icon: 'Play',
    // The runs workspace (/workflow-runs) filtered to this workflow.
    component: WorkflowRunsRedirect,
    showInNav: false,
  },
  {
    id: 'core:agents/cockpit',
    pluginId: 'core',
    path: '/agents/cockpit',
    title: 'Agent Cockpit',
    icon: 'Bot',
    component: AgentCockpitPage,
    showInNav: true,
    order: 58,
  },
];

/**
 * Register all core-owned pages into the PageRegistry.
 * Call this once during PluginLoader initialization, before plugins load,
 * so that core pages are available immediately.  Plugin pages with the
 * same path will override these if needed.
 */
export function registerCorePages(): void {
  const pageStore = usePageRegistry.getState();
  for (const page of CORE_PAGES) {
    pageStore.registerPage(page);
  }
}
